import React, { useState, useEffect } from "react";
import {
    Button,
    Col,
    DatePicker,
    Input,
    Row,
    Select,
    Space,
    Tag,
    TreeSelect,
    Typography,
    message,
} from "antd";
import {
    SearchOutlined,
    InfoCircleOutlined,
    UserOutlined,
    WalletOutlined,
    CheckCircleOutlined,
    FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { paymentAPIWithErrorHandling } from "../../../api/payment/payment";
import { collectionAPIWithErrorHandling } from "../../../api/collection/collection";
import { organizationAPIWithErrorHandling } from "../../../api/organization/organization";
import BrandedPageLayout from "../../../components/Dashboard/BrandedPageLayout";
import ListTableCard from "../../../components/common/ListTableCard";
import StatCard from "../../../components/common/StatCard";
import ListToolbar, {
    selectVisibleColumns,
    useListToolbar,
} from "../../../components/common/ListToolbar";
import ExportExcelButton from "../../../components/common/ExportExcelButton";
import { actionColumnDef } from "../../../components/common/RowActions";
import PaymentDetailDrawer from "./PaymentDetailDrawer";

const { RangePicker } = DatePicker;

const DEFAULT_VISIBLE_FILTERS = [];
const DEFAULT_VISIBLE_COLUMNS = [
    "customer_fee_id", "customer_name", "charge_target_number", "method", "payment_date",
    "amount", "note", "payUser", "created_at",
];

/**
 * Tài sản nhập từ Excel đôi khi lẫn xuống dòng hoặc chuỗi "\n" theo nghĩa đen
 * (vd "176/44/A3 \n(90/1) TRẦN BÁ GIAO") — gộp về một dòng. Cùng cách với file Excel.
 */
const assetText = (value) => String(value ?? "").replace(/(\\n|\s)+/g, " ").trim();

/** Cây đơn vị → dạng phẳng cho TreeSelect treeDataSimpleMode (cùng cách màn Tài sản). */
const flattenOrgTree = (nodes, parentId = 0) =>
    nodes.flatMap((node) => [
        { id: node.id, pId: parentId, value: node.id, title: node.name, isLeaf: !node.children_recursive?.length },
        ...flattenOrgTree(node.children_recursive || [], node.id),
    ]);

export default function PaymentTable() {
    const { Text } = Typography;

    // State management
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState([]);
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 15,
        total: 0,
    });
    const [searchText, setSearchText] = useState("");
    const [dateRange, setDateRange] = useState([null, null]);
    const [selectedMethod, setSelectedMethod] = useState("");
    // Đợt thu: chọn đợt cha thì backend lấy cả giao dịch của mọi đợt con.
    const [selectedCollection, setSelectedCollection] = useState(undefined);
    const [selectedOrg, setSelectedOrg] = useState(undefined);
    const [collectionOptions, setCollectionOptions] = useState([]);
    const [orgTree, setOrgTree] = useState([]);

    // Giao dịch đang xem chi tiết; null là drawer đóng.
    const [detailId, setDetailId] = useState(null);

    const [dashboard, setDashboard] = useState({
        total_amount: 0,
        total_transactions: 0,
        total_customers: 0,
        avg_transaction: 0,
    });

    const {
        visibleFilters, setVisibleFilters,
        visibleColumns, setVisibleColumns,
    } = useListToolbar({
        defaultVisibleFilters: DEFAULT_VISIBLE_FILTERS,
        defaultVisibleColumns: DEFAULT_VISIBLE_COLUMNS,
    });

    // Payment methods options
    const paymentMethods = [
        { value: "", label: "Tất cả" },
        { value: "CASH", label: "Tiền mặt" },
        { value: "BANK_TRANSFER", label: "Chuyển khoản" },
    ];

    // Load payment histories
    const loadData = async (params = {}) => {
        try {
            setLoading(true);

            // Prepare date params
            const dateParams = {};
            if (dateRange[0] && dateRange[1]) {
                dateParams.start_date = dateRange[0].format("YYYY-MM-DD");
                dateParams.end_date = dateRange[1].format("YYYY-MM-DD");
            }

            const response = await paymentAPIWithErrorHandling.getHistories({
                page: pagination.current,
                per_page: pagination.pageSize,
                search: searchText,
                method: selectedMethod,
                collection_id: selectedCollection,
                organization_id: selectedOrg,
                ...dateParams,
                ...params,
            });

            if (response.success) {
                const { data: responseData } = response;
                setData(responseData.data);
                setPagination({
                    ...pagination,
                    current: responseData.current_page,
                    total: responseData.total,
                });
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    const fetchDashboard = async (params = {}) => {
        try {
            setLoading(true);

            // Prepare date params
            const dateParams = {};
            if (dateRange[0] && dateRange[1]) {
                dateParams.start_date = dateRange[0].format("YYYY-MM-DD");
                dateParams.end_date = dateRange[1].format("YYYY-MM-DD");
            }

            const response = await paymentAPIWithErrorHandling.getDashboard({
                page: pagination.current,
                per_page: pagination.pageSize,
                search: searchText,
                method: selectedMethod,
                collection_id: selectedCollection,
                organization_id: selectedOrg,
                ...dateParams,
                ...params,
            });

            if (response.success) {
                setDashboard(response?.data);
            }
        } catch (error) {
            message.error(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Load data on component mount
    useEffect(() => {
        loadData();
        fetchDashboard();
        loadFilterOptions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Nguồn cho hai ô lọc. Cả hai API đã giới hạn theo quyền: nhân sự chỉ thấy đợt thu /
    // đơn vị của mình. Lỗi thì để ô trống, không chặn màn chính.
    const loadFilterOptions = async () => {
        try {
            const res = await collectionAPIWithErrorHandling.getCollectionOptions();
            setCollectionOptions((res.data?.data || []).map((c) => ({ value: c.id, label: c.name })));
        } catch (error) {
            setCollectionOptions([]);
        }

        try {
            const res = await organizationAPIWithErrorHandling.getTreeStructure();
            if (res.success) setOrgTree(flattenOrgTree(res.data || []));
        } catch (error) {
            setOrgTree([]);
        }
    };

    // Ô chọn (phương thức / đợt thu / đơn vị) áp ngay khi đổi. Trước đây phải bấm "Tìm kiếm"
    // nên chọn xong thanh công cụ đã báo "Bộ lọc (n)" mà bảng vẫn là dữ liệu chưa lọc — dễ
    // đọc nhầm là lọc sai. Truyền giá trị mới thẳng vào request vì setState chưa kịp có
    // hiệu lực ở lần gọi này. Ô tìm kiếm và khoảng ngày giữ cách cũ (Enter / Tìm kiếm).
    const applySelectFilter = (setter, param) => (value) => {
        setter(value);
        setPagination((prev) => ({ ...prev, current: 1 }));
        loadData({ page: 1, [param]: value });
        fetchDashboard({ [param]: value });
    };

    // Handle search
    const handleSearch = () => {
        setPagination({ ...pagination, current: 1 });
        loadData({ page: 1 });
        fetchDashboard();
    };

    // Handle reset
    const handleReset = () => {
        setSearchText("");
        setDateRange([null, null]);
        setSelectedMethod("");
        setSelectedCollection(undefined);
        setSelectedOrg(undefined);
        setPagination({ ...pagination, current: 1 });
        loadData({
            page: 1,
            search: "",
            method: "",
            collection_id: undefined,
            organization_id: undefined,
            start_date: undefined,
            end_date: undefined
        });

        fetchDashboard({
            page: 1,
            search: "",
            method: "",
            collection_id: undefined,
            organization_id: undefined,
            start_date: undefined,
            end_date: undefined
        });
    };

    // Handle pagination change
    const handleTableChange = (newPagination) => {
        setPagination(newPagination);
        loadData({
            page: newPagination.current,
            per_page: newPagination.pageSize,
        });
    };

    const renderPaymentMethod = (method) => {
        let color = "default";
        let text = method;

        switch (method) {
            case "CASH":
                color = "green";
                text = "Tiền mặt";
                break;
            case "BANK_TRANSFER":
                color = "blue";
                text = "Chuyển khoản";
                break;
            case "CARD":
                color = "orange";
                text = "Thẻ";
                break;
            case "MOMO":
                color = "purple";
                text = "Momo";
                break;
            case "ZALOPAY":
                color = "cyan";
                text = "ZaloPay";
                break;
            case "OTHER":
                color = "gray";
                text = "Khác";
                break;
            default:
                break;
        }

        return (
            <span
                style={{
                    padding: "4px 8px",
                    borderRadius: "4px",
                    fontSize: "12px",
                    backgroundColor:
                        color === "green"
                            ? "#EAF9F2"
                            : color === "blue"
                                ? "#F0F6FD"
                                : color === "orange"
                                    ? "#FFF5E8"
                                    : color === "purple"
                                        ? "#F0F1FF"
                                        : color === "cyan"
                                            ? "#e6fffb"
                                            : "#F8FAFC",
                    border: `1px solid ${color === "green"
                            ? "#B8E8D0"
                            : color === "blue"
                                ? "#B8D7F4"
                                : color === "orange"
                                    ? "#FFE0B8"
                                    : color === "purple"
                                        ? "#d3adf7"
                                        : color === "cyan"
                                            ? "#87e8de"
                                            : "#CBD5E1"
                        }`,
                    color:
                        color === "green"
                            ? "#128555"
                            : color === "blue"
                                ? "#0070D7"
                                : color === "orange"
                                    ? "#A8681E"
                                    : color === "purple"
                                        ? "#656DC7"
                                        : color === "cyan"
                                            ? "#13c2c2"
                                            : "#475569",
                }}
            >
                {text}
            </span>
        );
    };

    // ===== Định nghĩa cột =====
    const columnDefs = [
        {
            key: "index", label: "STT", fixed: true,
            column: {
                title: "STT", key: "index", width: 80, fixed: "left", align: "center",
                render: (_, record, index) =>
                    (pagination.current - 1) * pagination.pageSize + index + 1,
            },
        },
        {
            key: "customer_fee_id", label: "Mã phiếu thu",
            column: {
                // Mã dài 18 ký tự (HDCF261001W0MQWTUT) nằm trong Tag: 150px thì tràn/xuống dòng
                title: "Mã phiếu thu", dataIndex: "customer_fee_id", key: "customer_fee_id",
                width: 180, align: "center",
                render: (_, record) => {
                    return <Tag color="red">{record.customer_fee?.code || "-"}</Tag>;
                },
            },
        },
        {
            key: "customer_name", label: "Khách hàng",
            column: {
                // Cố định trái để cuộn ngang vẫn biết dòng của ai. rc-table chỉ cộng độ rộng các
                // cột ĐÃ fixed khi tính vị trí dính, nên cột này dính ngay sau STT; "Mã phiếu thu"
                // ở giữa vẫn cuộn bình thường, trượt xuống dưới cột này.
                title: "Khách hàng", dataIndex: "customer_name", key: "customer_name", width: 200,
                fixed: "left",
                render: (_, record) => {
                    const customer = record?.customer_fee?.customer;

                    if (!customer) return "-";

                    return (
                        <div>
                            <div className="font-medium">{customer.name}</div>
                            <Text type="secondary" className="text-xs">
                                {customer.phone}
                            </Text>
                        </div>
                    );
                },
            },
        },
        {
            // Khách nhiều tài sản có nhiều khoản thu — cột này cho biết giao dịch trả cho căn nào.
            // Tối đa ~50 ký tự: cho xuống dòng thay vì cắt, vì phần cuối (khu phố) thường là phần cần đọc.
            key: "charge_target_number", label: "Tài sản",
            column: {
                title: "Tài sản", key: "charge_target_number", width: 200,
                render: (_, record) => {
                    const text = assetText(record.customer_fee?.charge_target_number);
                    return text
                        ? <span style={{ fontSize: 13, lineHeight: 1.4, wordBreak: "break-word" }}>{text}</span>
                        : "-";
                },
            },
        },
        {
            key: "method", label: "Loại thanh toán",
            column: {
                title: "Loại thanh toán", dataIndex: "method", key: "method",
                width: 150, align: "center",
                render: (method) => renderPaymentMethod(method),
            },
        },
        {
            key: "payment_date", label: "Ngày giao dịch",
            column: {
                title: "Ngày giao dịch", dataIndex: "payment_date", key: "payment_date", width: 120,
                render: (payment_date) => dayjs(payment_date).format("DD/MM/YYYY") || "-",
            },
        },
        {
            key: "amount", label: "Số tiền",
            column: {
                title: "Số tiền", dataIndex: "amount", key: "amount", width: 150, align: "right",
                render: (_, record) => {
                    const total = Number(record.amount || 0);
                    return (
                        <Text strong className="text-warning-600">
                            {total.toLocaleString("vi-VN")} ₫
                        </Text>
                    );
                },
            },
        },
        {
            key: "note", label: "Lưu ý",
            column: {
                // Ghi chú đối soát dài ~90-110 ký tự (vd "Đối soát 03/09/2026 11:38: gán về khoản
                // thu HDCF… theo mã hóa đơn ConekONESYNC… trong lịch sử QR"). Bản cũ `ellipsis`
                // một dòng ở 200px chỉ còn "Đối soát 03/09/2026 1…". Giờ rộng hơn, cho 2 dòng
                // chữ nhỏ, dài hơn nữa thì "…" và rê chuột xem đủ.
                title: "Lưu ý", dataIndex: "note", key: "note", width: 280,
                render: (text) =>
                    text ? (
                        <Typography.Paragraph
                            ellipsis={{ rows: 2, tooltip: text }}
                            style={{ marginBottom: 0, fontSize: 12, lineHeight: 1.45 }}
                            type="secondary"
                        >
                            {text}
                        </Typography.Paragraph>
                    ) : "-",
            },
        },
        {
            key: "payUser", label: "Người thanh toán",
            column: {
                title: "Người thanh toán", dataIndex: "payUser", key: "payUser",
                width: 180, align: "center",
                render: (_, record) => {
                    const payUserName = record.pay_user?.username ?? '_';
                    return (
                        <Text strong className="text-warning-600">{payUserName}</Text>
                    );
                },
            },
        },
        {
            key: "created_at", label: "Thời gian lấy báo cáo",
            column: {
                title: "Thời gian lấy báo cáo", dataIndex: "created_at", key: "created_at", width: 180,
                render: (text) => dayjs(text).format("DD/MM/YYYY HH:mm:ss") || "-",
            },
        },
        actionColumnDef((record) => [
            {
                key: "detail",
                label: "Xem chi tiết",
                icon: <InfoCircleOutlined />,
                tone: "view",
                onClick: () => setDetailId(record.id),
            },
        ]),
    ];

    // ===== Định nghĩa bộ lọc =====
    const filterDefs = [
        {
            key: "search", label: "Tìm kiếm", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Input
                    placeholder="Nhập để tìm kiếm..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onPressEnter={handleSearch}
                    allowClear
                />
            ),
        },
        {
            key: "method", label: "Phương thức", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Select
                    style={{ width: "100%" }}
                    placeholder="Chọn phương thức"
                    value={selectedMethod}
                    onChange={applySelectFilter(setSelectedMethod, "method")}
                    options={paymentMethods}
                    allowClear
                />
            ),
        },
        {
            key: "collection", label: "Đợt thu", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <Select
                    style={{ width: "100%" }}
                    placeholder="Chọn đợt thu"
                    value={selectedCollection}
                    onChange={applySelectFilter(setSelectedCollection, "collection_id")}
                    options={collectionOptions}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                />
            ),
        },
        {
            key: "organization", label: "Đơn vị", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <TreeSelect
                    treeDataSimpleMode
                    style={{ width: "100%" }}
                    placeholder="Chọn đơn vị"
                    value={selectedOrg}
                    onChange={applySelectFilter(setSelectedOrg, "organization_id")}
                    treeData={orgTree}
                    styles={{ popup: { root: { maxHeight: 400, overflow: "auto" } } }}
                    showSearch
                    filterTreeNode={(input, item) =>
                        String(item.title).toLowerCase().includes(input.toLowerCase())
                    }
                    allowClear
                />
            ),
        },
        {
            key: "dateRange", label: "Khoảng ngày", span: { xs: 24, sm: 12, md: 8 },
            field: (
                <RangePicker
                    style={{ width: "100%" }}
                    format="DD/MM/YYYY"
                    placeholder={["Từ ngày", "Đến ngày"]}
                    value={dateRange}
                    onChange={(dates) => setDateRange(dates || [null, null])}
                />
            ),
        },
    ];

    const columns = selectVisibleColumns(columnDefs, visibleColumns);
    const hasActiveFilter = !!(
        searchText || selectedMethod || selectedCollection || selectedOrg ||
        (dateRange && dateRange[0] && dateRange[1])
    );

    // Cùng bộ lọc với loadData để file xuất ra khớp đúng danh sách đang xem.
    // Lưu ý: lấy theo bộ lọc đã áp dụng (state), không phải chữ đang gõ dở trong ô tìm
    // kiếm — màn này chỉ lọc khi bấm "Tìm kiếm" nên bảng cũng đang hiển thị như vậy.
    const exportParams = {
        ...(searchText ? { search: searchText } : {}),
        ...(selectedMethod ? { method: selectedMethod } : {}),
        ...(selectedCollection ? { collection_id: selectedCollection } : {}),
        ...(selectedOrg ? { organization_id: selectedOrg } : {}),
        ...(dateRange?.[0] && dateRange?.[1]
            ? {
                  start_date: dateRange[0].format("YYYY-MM-DD"),
                  end_date: dateRange[1].format("YYYY-MM-DD"),
              }
            : {}),
    };

    return (
        <BrandedPageLayout
            title="Lịch sử thanh toán"
            subtitle="Theo dõi các giao dịch thanh toán của khách hàng."
            breadcrumbItems={[{ title: "Lịch sử thanh toán" }]}
        >
            {/* Mini Dashboard - 4 card */}
            <Row gutter={[16, 16]} className="mb-4">
                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<FileTextOutlined />}
                        tone="brand"
                        label="Tổng giao dịch"
                        value={dashboard.total_transactions ?? 0}
                    />
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<UserOutlined />}
                        tone="info"
                        label="Tổng khách hàng"
                        value={dashboard.total_customers ?? 0}
                    />
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<WalletOutlined />}
                        tone="success"
                        label="Tổng tiền giao dịch"
                        value={`${Number(dashboard.total_amount ?? 0).toLocaleString("vi-VN")} ₫`}
                    />
                </Col>

                <Col xs={24} sm={12} lg={6}>
                    <StatCard
                        icon={<CheckCircleOutlined />}
                        tone="neutral"
                        label="Trung bình / giao dịch"
                        value={`${Number(dashboard.avg_transaction ?? 0).toLocaleString("vi-VN")} ₫`}
                    />
                </Col>
            </Row>

            <ListToolbar
                filterDefs={filterDefs}
                visibleFilters={visibleFilters}
                onChangeVisibleFilters={setVisibleFilters}
                columnDefs={columnDefs}
                visibleColumns={visibleColumns}
                onChangeVisibleColumns={setVisibleColumns}
                defaultVisibleColumns={DEFAULT_VISIBLE_COLUMNS}
                hasActiveFilter={hasActiveFilter}
                onClearFilter={handleReset}
                onReload={() => { loadData(); fetchDashboard(); }}
                extra={
                    <Space size="small">
                        <ExportExcelButton
                            url="/api/v1/payments/histories/export"
                            params={exportParams}
                            fileName="lich_su_thanh_toan.xlsx"
                            actions={["payments.histories"]}
                            type="default"
                        />
                        <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                            Tìm kiếm
                        </Button>
                    </Space>
                }
            />

            <ListTableCard
                columns={columns}
                dataSource={data}
                loading={loading}
                pagination={{
                    current: pagination.current,
                    pageSize: pagination.pageSize,
                    total: pagination.total,
                }}
                onChange={handleTableChange}
                emptyText="Không có dữ liệu"
            />

            <PaymentDetailDrawer
                open={detailId !== null}
                paymentId={detailId}
                onClose={() => setDetailId(null)}
            />
        </BrandedPageLayout>
    );
}
